import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Orange } from 'wobi-web-common/dist/Colors';

const formatDate = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

const styles = {
  card: {
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    borderRadius: '16px',
    p: '4px 16px',
    mb: 2,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    py: 1.75,
  },
  divider: {
    borderBottom: 1,
    borderColor: 'divider',
  },
  infoGroup: { display: 'flex', alignItems: 'center', gap: 1.5 },
  carIcon: {
    width: 44,
    height: 44,
    borderRadius: '12px',
    bgcolor: 'secondary.main',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'secondary.contrastText',
    flexShrink: 0,
  },
  calendarIcon: {
    width: 40,
    height: 40,
    borderRadius: '10px',
    bgcolor: Orange[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  label: (theme) => ({
    fontSize: theme.typography.pxToRem(12),
    color: 'text.secondary',
    lineHeight: 1.2,
  }),
  value: (theme) => ({
    fontWeight: 800,
    fontSize: theme.typography.pxToRem(16),
    color: 'text.primary',
  }),
  licensePlate: (theme) => ({
    border: 1,
    borderColor: 'divider',
    borderRadius: '8px',
    px: 1.5,
    py: 0.75,
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 600,
    letterSpacing: 1,
    color: 'text.primary',
    whiteSpace: 'nowrap',
  }),
  daysIndicator: (theme) => ({
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    px: 2,
    py: 0.75,
    borderRadius: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    whiteSpace: 'nowrap',
  }),
};

const VehicleSummaryCard = ({
  vehicleInfo,
  policyEndDate,
  daysUntilExpiry,
  labels,
}) => {
  const vehicleLabel = [vehicleInfo.manufacturer, vehicleInfo.model]
    .filter(Boolean)
    .join(' ');

  return (
    <Box sx={styles.card}>
      <Box sx={[styles.row, styles.divider]}>
        <Box sx={styles.infoGroup}>
          <Box sx={styles.carIcon}>
            <DirectionsCarIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={styles.label}>{labels.vehicle}</Typography>
            <Typography sx={styles.value}>
              {vehicleLabel} {vehicleInfo.year && `· ${vehicleInfo.year}`}
            </Typography>
          </Box>
        </Box>
        {vehicleInfo.licensePlate && (
          <Box component='span' sx={styles.licensePlate}>
            {vehicleInfo.licensePlate}
          </Box>
        )}
      </Box>

      <Box sx={styles.row}>
        <Box sx={styles.infoGroup}>
          <Box sx={styles.calendarIcon}>
            <CalendarTodayIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography sx={styles.label}>{labels.policyEndDate}</Typography>
            <Typography sx={styles.value}>
              {formatDate(policyEndDate)}
            </Typography>
          </Box>
        </Box>
        {daysUntilExpiry != null && (
          <Box sx={styles.daysIndicator}>
            <AccessTimeIcon sx={{ fontSize: 16 }} />
            {labels.daysRemaining}
          </Box>
        )}
      </Box>
    </Box>
  );
};

VehicleSummaryCard.propTypes = {
  daysUntilExpiry: PropTypes.number,
  labels: PropTypes.shape({
    daysRemaining: PropTypes.string.isRequired,
    policyEndDate: PropTypes.string.isRequired,
    vehicle: PropTypes.string.isRequired,
  }).isRequired,
  policyEndDate: PropTypes.string,
  vehicleInfo: PropTypes.shape({
    licensePlate: PropTypes.string,
    manufacturer: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.number,
  }).isRequired,
};

export default VehicleSummaryCard;
